import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import { Card } from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Label, LabelList,
} from "recharts";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { IoTrendingUp, IoWallet, IoBarChart, IoCheckmarkCircle } from "react-icons/io5";
import { useFinOps } from "context/FinOpsContext";
import { fmtShort, fmt } from "data/finopsData";

const TAB = { PL: "P&L", BS: "Balance Sheet" };

const CHART_BG   = "transparent";
const AXIS_COLOR = "#556581";
const GRID_COLOR = "#2D3748";

function fmtK(v) {
  v = Number(v);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(0)}K`;
  return v.toFixed(0);
}

// Consistent unit formatter — picks M or K based on the largest value in a dataset
function makeChartFmt(values) {
  const max = Math.max(...values.map(Math.abs).filter(Boolean));
  if (max >= 1_000_000) return (v) => v ? `${(v / 1_000_000).toFixed(1)}M` : "";
  return (v) => v ? `${(v / 1_000).toFixed(0)}K` : "";
}

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  const visible = payload.filter((p) => p.value && p.value !== 0);
  if (!visible.length) return null;
  const fmt = formatter || fmtK;
  return (
    <VuiBox sx={{ background: "rgba(13,20,42,0.95)", border: "1px solid #2D3748", borderRadius: "8px", p: "10px 14px" }}>
      <VuiTypography variant="caption" color="text" display="block" mb="4px">{label}</VuiTypography>
      {visible.map((p, i) => (
        <VuiBox key={i} display="flex" justifyContent="space-between" gap={2}>
          <VuiTypography variant="caption" color="text">{p.name}</VuiTypography>
          <VuiTypography variant="button" fontWeight="bold" sx={{ color: p.fill || p.color }}>
            AED {fmt(p.value)}
          </VuiTypography>
        </VuiBox>
      ))}
    </VuiBox>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <VuiBox sx={{ background: "rgba(13,20,42,0.95)", border: "1px solid #2D3748", borderRadius: "8px", p: "10px 14px" }}>
      <VuiTypography variant="caption" color="text" display="block">{p.name}</VuiTypography>
      <VuiTypography variant="button" fontWeight="bold" sx={{ color: p.payload.color }}>
        AED {fmtK(p.value)}
      </VuiTypography>
      <VuiTypography variant="caption" color="text" display="block">
        {p.payload.pct}% of assets
      </VuiTypography>
    </VuiBox>
  );
};

function StatBox({ label, value, color, sub }) {
  return (
    <VuiBox
      mb={1.5}
      p={1.5}
      sx={{ background: "rgba(255,255,255,0.04)", borderRadius: "10px", borderLeft: `3px solid ${color}` }}
    >
      <VuiTypography variant="caption" color="text" display="block">{label}</VuiTypography>
      <VuiTypography variant="h6" fontWeight="bold" sx={{ color }}>
        {fmt(value)}
      </VuiTypography>
      {sub && <VuiTypography variant="caption" color="text">{sub}</VuiTypography>}
    </VuiBox>
  );
}

function Dashboard() {
  const { fsData, company, period, isUploaded } = useFinOps();
  const { kpis, plLines, bsLines } = fsData;
  const [tab, setTab]               = useState(TAB.BS);
  const [analysis, setAnalysis]     = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!isUploaded) return;
    setAnalysisLoading(true);
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ company, period, kpis, plLines, bsLines }),
    })
      .then((r) => r.json())
      .then((d) => { setAnalysis(d); setAnalysisLoading(false); })
      .catch(() => setAnalysisLoading(false));
  }, [isUploaded, company]);

  const downloadBoardPDF = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company, period, kpis, plLines, bsLines }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Server error: ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Board_Report_${company.replace(/\s+/g, "_")}.pdf`;
      a.click();
    } catch (err) { alert(`PDF generation failed: ${err.message}`); }
  };

  const downloadBoardPPT = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/generate-ppt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company, period, kpis, plLines, bsLines, theme: "navy" }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Server error: ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Board_Report_${company.replace(/\s+/g, "_")}.pptx`;
      a.click();
    } catch (err) { alert(`PPT generation failed: ${err.message}`); }
  };

  // ── Chart data ─────────────────────────────────────────────────────────────
  const plBarData = [
    { name: "Revenue",      value: kpis.revenue,      color: "#48BB78" },
    { name: "Gross Profit", value: kpis.grossProfit,  color: "#4FD1C5" },
    { name: "Net Profit",   value: Math.max(kpis.netProfit, 0), color: "#0075FF" },
  ];
  const fmtPL = makeChartFmt([kpis.revenue, kpis.grossProfit, kpis.netProfit]);
  const fmtBS = makeChartFmt([kpis.totalAssets, kpis.totalLiabilities, kpis.totalEquity]);

  const expenseData = plLines
    .filter((l) => l.group === "Expense")
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 6)
    .map((l) => ({ name: l.lineItem.length > 18 ? l.lineItem.slice(0, 16) + "…" : l.lineItem, value: l.amount, color: "#C9A84C" }));

  const totalAssets = kpis.totalAssets || 1;
  // Assets = Liabilities + Equity — show only the two components
  const bsDonutData = [
    { name: "Liabilities", value: Math.max(kpis.totalLiabilities, 0), color: "#C9A84C", pct: Math.round(kpis.totalLiabilities / totalAssets * 100) },
    { name: "Equity",      value: Math.max(kpis.totalEquity, 0),      color: "#4FD1C5", pct: Math.round(kpis.totalEquity / totalAssets * 100) },
  ];

  const tabBtn = (t) => ({
    variant: tab === t ? "contained" : "outlined",
    color: "info",
    size: "small",
    onClick: () => setTab(t),
    sx: { mr: 1, minWidth: "120px" },
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* Upload prompt */}
        {!isUploaded && (
          <VuiBox mb={2} px={2} py={1.5} sx={{
            background: "rgba(0,117,255,0.08)",
            border: "1px solid #0075ff",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <VuiButton color="info" variant="contained" size="small" onClick={() => history.push("/upload")}>
              Upload Trial Balance to get started →
            </VuiButton>
          </VuiBox>
        )}

        {/* KPI Cards */}
        <VuiBox mb={3}>
          <Grid container spacing={3} alignItems="stretch">
            {[
              { title: "Total Revenue",  count: fmtShort(kpis.revenue),     pct: "turnover",                                               icon: <IoTrendingUp size="22px" color="white" /> },
              { title: "Net Profit",     count: fmtShort(kpis.netProfit),   pct: `${kpis.profitMargin}% margin`,                            icon: <IoWallet size="22px" color="white" /> },
              { title: "Total Assets",   count: fmtShort(kpis.totalAssets), pct: "asset base",                                              icon: <IoBarChart size="22px" color="white" /> },
              { title: "Gross Profit",   count: fmtShort(kpis.grossProfit), pct: `${((kpis.grossProfit/kpis.revenue)*100).toFixed(1)}% GP`, icon: <IoCheckmarkCircle size="22px" color="white" /> },
            ].map(({ title, count, pct, icon }) => (
              <Grid item xs={12} md={6} xl={3} sx={{ display: "flex" }} key={title}>
                <MiniStatisticsCard
                  title={{ text: title, fontWeight: "regular" }}
                  count={count}
                  percentage={{ color: "success", text: pct }}
                  icon={{ color: "info", component: icon }}
                />
              </Grid>
            ))}
          </Grid>
        </VuiBox>

        {/* AI Analysis */}
        {isUploaded && (
          <Card sx={{ mb: 3 }}>
            <VuiBox>
              <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <VuiBox display="flex" alignItems="center" gap={1.5}>
                  <VuiTypography variant="lg" color="white" fontWeight="bold">AI Financial Analysis</VuiTypography>
                  {analysis && (
                    <VuiBox px={1.5} py={0.4} sx={{
                      borderRadius: "12px",
                      background: analysis.health === "Healthy" ? "rgba(72,187,120,0.2)" : analysis.health === "Caution" ? "rgba(255,181,71,0.2)" : "rgba(227,26,26,0.2)",
                      border: analysis.health === "Healthy" ? "1px solid #48BB78" : analysis.health === "Caution" ? "1px solid #FFB547" : "1px solid #E31A1A",
                    }}>
                      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: analysis.health === "Healthy" ? "#48BB78" : analysis.health === "Caution" ? "#FFB547" : "#E31A1A" }}>
                        {analysis.health}
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
                <VuiTypography variant="caption" color="text">Financial Analysis · {company}</VuiTypography>
              </VuiBox>
              {analysisLoading && <VuiTypography variant="button" color="text">Analysing financials…</VuiTypography>}
              {analysis && !analysisLoading && (
                <Grid container spacing={2}>
                  {analysis.points.map((pt, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <VuiBox display="flex" alignItems="flex-start" gap={1}>
                        <VuiBox sx={{ minWidth: "6px", height: "6px", borderRadius: "50%", background: "#0075ff", mt: "7px", flexShrink: 0 }} />
                        <VuiTypography variant="button" color="text" sx={{ lineHeight: 1.6 }}>
                          <span style={{ color: "#fff", fontWeight: 600 }}>{pt.label}: </span>{pt.text}
                        </VuiTypography>
                      </VuiBox>
                    </Grid>
                  ))}
                </Grid>
              )}
            </VuiBox>
          </Card>
        )}

        {/* Charts Panel */}
        <Card>
          <VuiBox>
            {/* Header */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <VuiBox>
                <VuiButton {...tabBtn(TAB.BS)}>Balance Sheet</VuiButton>
                <VuiButton {...tabBtn(TAB.PL)}>P&amp;L</VuiButton>
              </VuiBox>
              <VuiBox display="flex" gap={1}>
                {isUploaded && (
                  <VuiButton color="warning" variant="contained" size="small" onClick={downloadBoardPDF}>
                    Download Board PDF
                  </VuiButton>
                )}
                {isUploaded && (
                  <VuiButton color="info" variant="contained" size="small" onClick={downloadBoardPPT}>
                    Download Board PPT
                  </VuiButton>
                )}
                <VuiButton color="success" variant="contained" size="small" onClick={() => history.push("/upload")}>
                  Upload TB →
                </VuiButton>
              </VuiBox>
            </VuiBox>

            {/* ── Balance Sheet tab ── */}
            {tab === TAB.BS && (
              <Grid container spacing={3} alignItems="center">
                {/* Balanced bar chart */}
                <Grid item xs={12} md={7}>
                  {/* BALANCED badge — real calculation, only shown after TB upload */}
                  {(() => {
                    const diff = Math.abs(kpis.totalAssets - kpis.totalLiabilities - kpis.totalEquity);
                    const isBalanced = diff < 1;
                    return (
                      <VuiBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        <VuiTypography variant="button" color="white" fontWeight="bold">
                          Balance Sheet
                        </VuiTypography>
                        {isUploaded && (
                          <VuiBox px={1.2} py={0.3} sx={{ borderRadius: "20px", background: isBalanced ? "rgba(72,187,120,0.15)" : "rgba(252,129,129,0.15)", border: `1px solid ${isBalanced ? "#48BB78" : "#FC8181"}` }}>
                            <VuiTypography variant="caption" sx={{ color: isBalanced ? "#48BB78" : "#FC8181", fontWeight: 700 }}>
                              {isBalanced ? "✓ BALANCED" : `✗ Off by AED ${Math.round(diff).toLocaleString()}`}
                            </VuiTypography>
                          </VuiBox>
                        )}
                      </VuiBox>
                    );
                  })()}

                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={[
                        { name: "Total Assets",   assets: kpis.totalAssets, liabilities: 0,                   equity: 0 },
                        { name: "Funded By",      assets: 0,                liabilities: kpis.totalLiabilities, equity: kpis.totalEquity },
                      ]}
                      margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
                      barCategoryGap="30%"
                    >
                      <XAxis dataKey="name" tick={{ fill: "#A0B4D0", fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtBS} tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip formatter={fmtBS} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="assets"      name="Assets"      stackId="a" fill="#0075FF" radius={[6,6,0,0]} maxBarSize={90}>
                        <LabelList dataKey="assets" position="top" formatter={(v) => v ? fmtBS(v) : ""} style={{ fill: "#fff", fontSize: 11, fontWeight: 600 }} />
                      </Bar>
                      <Bar dataKey="equity"      name="Equity"      stackId="a" fill="#4FD1C5" radius={[0,0,0,0]} maxBarSize={90}>
                        <LabelList dataKey="equity"      position="insideBottom" offset={8} formatter={(v) => v ? `Equity ${fmtBS(v)}` : ""} style={{ fill: "#fff", fontSize: 10 }} />
                      </Bar>
                      <Bar dataKey="liabilities" name="Liabilities" stackId="a" fill="#C9A84C" radius={[6,6,0,0]} maxBarSize={90}>
                        <LabelList dataKey="liabilities" position="top"          formatter={(v) => v ? fmtBS(kpis.totalLiabilities + kpis.totalEquity) : ""} style={{ fill: "#fff", fontSize: 11, fontWeight: 600 }} />
                        <LabelList dataKey="liabilities" position="insideBottom" offset={8} formatter={(v) => v ? `Liab. ${fmtBS(v)}` : ""} style={{ fill: "#fff", fontSize: 10 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Legend */}
                  <VuiBox display="flex" justifyContent="center" gap={3} mt={1}>
                    {[["#0075FF","Assets"],["#4FD1C5","Equity"],["#C9A84C","Liabilities"]].map(([col,lbl]) => (
                      <VuiBox key={lbl} display="flex" alignItems="center" gap={0.8}>
                        <VuiBox sx={{ width: 10, height: 10, borderRadius: "2px", background: col }} />
                        <VuiTypography variant="caption" color="text">{lbl}</VuiTypography>
                      </VuiBox>
                    ))}
                  </VuiBox>
                </Grid>

                {/* Stat boxes */}
                <Grid item xs={12} md={5}>
                  <StatBox label="Total Assets"      value={kpis.totalAssets}      color="#0075FF" sub="= Liabilities + Equity" />
                  <StatBox label="Total Liabilities" value={kpis.totalLiabilities} color="#C9A84C" sub={`${Math.round(kpis.totalLiabilities/totalAssets*100)}% of total assets`} />
                  <StatBox label="Total Equity"      value={kpis.totalEquity}      color="#4FD1C5" sub={`${Math.round(kpis.totalEquity/totalAssets*100)}% of total assets`} />
                  <StatBox label="Net Profit"        value={kpis.netProfit}        color="#48BB78" sub={`${kpis.profitMargin}% net margin`} />
                </Grid>
              </Grid>
            )}

            {/* ── P&L tab ── */}
            {tab === TAB.PL && (
              <Grid container spacing={3}>
                {/* Profitability bar chart */}
                <Grid item xs={12} md={6}>
                  <VuiTypography variant="button" color="text" fontWeight="bold" display="block" mb={1}>
                    Profitability Overview
                  </VuiTypography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={plBarData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fill: AXIS_COLOR, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtPL} tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip formatter={fmtPL} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
                        {plBarData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        <LabelList dataKey="value" position="top" formatter={(v) => fmtPL(v)} style={{ fill: "#fff", fontSize: 10 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
                {/* Expense breakdown */}
                <Grid item xs={12} md={6}>
                  <VuiTypography variant="button" color="text" fontWeight="bold" display="block" mb={1}>
                    Top Expenses
                  </VuiTypography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={expenseData} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                      <XAxis type="number" tickFormatter={fmtPL} tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={false} tickLine={false} width={100} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {expenseData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        <LabelList dataKey="value" position="right" formatter={fmtPL} style={{ fill: "#fff", fontSize: 10 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Net profit row */}
                  <VuiBox mt={2} pt={2} sx={{ borderTop: "1px solid #2D2E5F" }}>
                    <VuiBox display="flex" justifyContent="space-between" px={1} py={1} sx={{ background: "rgba(72,187,120,0.12)", borderRadius: "8px" }}>
                      <VuiTypography variant="button" color="success" fontWeight="bold">Net Profit</VuiTypography>
                      <VuiTypography variant="button" color="success" fontWeight="bold">{fmt(kpis.netProfit)}</VuiTypography>
                    </VuiBox>
                  </VuiBox>
                </Grid>
              </Grid>
            )}

          </VuiBox>
        </Card>

      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
